/**
 * Firebase Admin Service
 * Real Firestore operations for admin panel
 * Replaces mockData.ts with live database integration
 */

import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  updateDoc, 
  query, 
  where, 
  orderBy,
  limit,
  Timestamp,
  addDoc,
  increment
} from 'firebase/firestore';
import { db } from '../../../config/firebaseConfig';
import { User, Membership, PromoCode, Analytics } from './mockData';

/**
 * Convert Firestore timestamp to ISO string
 */
const toISOString = (timestamp: any): string => {
  if (!timestamp) return new Date().toISOString();
  if (timestamp.toDate) return timestamp.toDate().toISOString();
  if (timestamp instanceof Timestamp) return timestamp.toDate().toISOString();
  if (typeof timestamp === 'string') return timestamp;
  return new Date(timestamp).toISOString();
};

/**
 * Convert Firestore document to User interface
 */
const docToUser = (docData: any, id: string): User => {
  const data = docData.data();
  return {
    id: id,
    username: data.username || '',
    email: data.email || '',
    fullName: data.fullName || data.name || '',
    role: data.role || 'user',
    status: data.status || 'active',
    membershipType: data.membershipType || 'free',
    createdAt: toISOString(data.createdAt),
    lastLogin: toISOString(data.lastLogin),
    totalSessions: data.totalSessions || 0,
    averageSessionTime: data.averageSessionTime || 0,
    appVersion: data.appVersion || 'unknown',
    platform: data.platform || 'Web'
  };
};

/**
 * Firebase Admin API
 * All operations interact with Firestore collections
 */
export const firebaseAdminAPI = {
  // ========== User Management ==========
  
  /**
   * Get all users from Firestore
   */
  getUsers: async (): Promise<User[]> => {
    const timestamp = new Date().toISOString();
    try {
      console.log(`[AUDIT] ${timestamp} Fetching all users from Firestore`);
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      const users = snapshot.docs.map(doc => docToUser(doc, doc.id));
      console.log(`[AUDIT] ${timestamp} Retrieved ${users.length} users from Firestore`);
      return users;
    } catch (error) {
      console.error(`[AUDIT] ${timestamp} Error fetching users:`, error);
      throw error;
    }
  },

  /**
   * Search users by query string
   * Note: Firestore doesn't support full-text search natively
   * For production, consider Algolia or similar search service
   */
  searchUsers: async (queryText: string): Promise<User[]> => {
    const timestamp = new Date().toISOString();
    try {
      console.log(`[AUDIT] ${timestamp} Searching users with query: "${queryText}"`);
      const usersRef = collection(db, 'users');
      // Fetch all users and filter client-side
      // For production, use Algolia or Firestore full-text search extension
      const snapshot = await getDocs(usersRef);
      const lowerQuery = queryText.toLowerCase();
      const filtered = snapshot.docs
        .map(doc => docToUser(doc, doc.id))
        .filter(user => 
          user.username.toLowerCase().includes(lowerQuery) ||
          user.email.toLowerCase().includes(lowerQuery) ||
          user.fullName.toLowerCase().includes(lowerQuery)
        );
      console.log(`[AUDIT] ${timestamp} Search returned ${filtered.length} results`);
      return filtered;
    } catch (error) {
      console.error(`[AUDIT] ${timestamp} Error searching users:`, error);
      throw error;
    }
  },

  /**
   * Get user by ID
   */
  getUserById: async (id: string): Promise<User | null> => {
    const timestamp = new Date().toISOString();
    try {
      console.log(`[AUDIT] ${timestamp} Fetching user by ID: ${id}`);
      const userRef = doc(db, 'users', id);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const user = docToUser(userSnap, id);
        console.log(`[AUDIT] ${timestamp} User found: ${user.username}`);
        return user;
      }
      console.log(`[AUDIT] ${timestamp} User not found: ${id}`);
      return null;
    } catch (error) {
      console.error(`[AUDIT] ${timestamp} Error fetching user:`, error);
      throw error;
    }
  },

  /**
   * Update user status (active/banned/suspended)
   */
  updateUserStatus: async (userId: string, status: 'active' | 'banned' | 'suspended'): Promise<boolean> => {
    const timestamp = new Date().toISOString();
    try {
      console.log(`[AUDIT] ${timestamp} Updating user status: ${userId} -> ${status}`);
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { 
        status,
        updatedAt: Timestamp.now()
      });
      console.log(`[AUDIT] ${timestamp} User status updated successfully`);
      return true;
    } catch (error) {
      console.error(`[AUDIT] ${timestamp} Error updating user status:`, error);
      return false;
    }
  },

  /**
   * Reset user password
   * Note: This should trigger a Cloud Function that:
   * 1. Generates temporary password
   * 2. Updates Firebase Auth
   * 3. Sends email with temp password
   * For now, returns mock response
   */
  resetPassword: async (userId: string): Promise<{ success: boolean; newPassword?: string }> => {
    const timestamp = new Date().toISOString();
    try {
      console.log(`[AUDIT] ${timestamp} Resetting password for user: ${userId}`);
      // TODO: Call Cloud Function to reset password
      // For now, generate temp password
      const newPassword = `TempPass${Math.random().toString(36).substring(7)}`;
      console.log(`[AUDIT] ${timestamp} Password reset - temp password generated`);
      return { success: true, newPassword };
    } catch (error) {
      console.error(`[AUDIT] ${timestamp} Error resetting password:`, error);
      return { success: false };
    }
  },

  // ========== Membership Management ==========

  /**
   * Get all memberships
   */
  getMemberships: async (): Promise<Membership[]> => {
    const timestamp = new Date().toISOString();
    try {
      console.log(`[AUDIT] ${timestamp} Fetching memberships from Firestore`);
      const membershipsRef = collection(db, 'memberships');
      const snapshot = await getDocs(membershipsRef);
      const memberships = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          userId: data.userId,
          type: data.type,
          startDate: toISOString(data.startDate),
          endDate: data.endDate ? toISOString(data.endDate) : null,
          autoRenew: data.autoRenew || false
        };
      });
      console.log(`[AUDIT] ${timestamp} Retrieved ${memberships.length} memberships`);
      return memberships;
    } catch (error) {
      console.error(`[AUDIT] ${timestamp} Error fetching memberships:`, error);
      throw error;
    }
  },

  /**
   * Update user membership type
   */
  updateMembership: async (userId: string, type: 'free' | 'premium' | 'enterprise'): Promise<boolean> => {
    const timestamp = new Date().toISOString();
    try {
      console.log(`[AUDIT] ${timestamp} Updating membership: ${userId} -> ${type}`);
      
      // Update user's membership type
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { 
        membershipType: type,
        updatedAt: Timestamp.now()
      });

      // Update or create membership record
      const membershipsRef = collection(db, 'memberships');
      const q = query(membershipsRef, where('userId', '==', userId));
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        // Update existing membership
        const membershipDoc = snapshot.docs[0];
        await updateDoc(doc(db, 'memberships', membershipDoc.id), {
          type,
          updatedAt: Timestamp.now()
        });
      } else {
        // Create new membership record
        await addDoc(membershipsRef, {
          userId,
          type,
          startDate: Timestamp.now(),
          endDate: null,
          autoRenew: true,
          createdAt: Timestamp.now()
        });
      }
      
      console.log(`[AUDIT] ${timestamp} Membership updated successfully`);
      return true;
    } catch (error) {
      console.error(`[AUDIT] ${timestamp} Error updating membership:`, error);
      return false;
    }
  },

  // ========== Promo Code Management ==========

  /**
   * Get all promo codes
   */
  getPromoCodes: async (): Promise<PromoCode[]> => {
    const timestamp = new Date().toISOString();
    try {
      console.log(`[AUDIT] ${timestamp} Fetching promo codes from Firestore`);
      const promosRef = collection(db, 'promoCodes');
      const snapshot = await getDocs(promosRef);
      const promoCodes = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          code: data.code,
          discount: data.discount,
          validFrom: toISOString(data.validFrom),
          validUntil: toISOString(data.validUntil),
          maxUses: data.maxUses,
          currentUses: data.currentUses || 0,
          assignedTo: data.assignedTo || []
        };
      });
      console.log(`[AUDIT] ${timestamp} Retrieved ${promoCodes.length} promo codes`);
      return promoCodes;
    } catch (error) {
      console.error(`[AUDIT] ${timestamp} Error fetching promo codes:`, error);
      throw error;
    }
  },

  /**
   * Assign promo code to user
   */
  assignPromoCode: async (promoCodeId: string, userId: string): Promise<boolean> => {
    const timestamp = new Date().toISOString();
    try {
      console.log(`[AUDIT] ${timestamp} Assigning promo code ${promoCodeId} to user ${userId}`);
      const promoRef = doc(db, 'promoCodes', promoCodeId);
      const promoSnap = await getDoc(promoRef);
      
      if (!promoSnap.exists()) {
        console.error(`[AUDIT] ${timestamp} Promo code not found: ${promoCodeId}`);
        return false;
      }
      
      const data = promoSnap.data();
      const assignedTo = data.assignedTo || [];
      
      if (assignedTo.includes(userId)) {
        console.log(`[AUDIT] ${timestamp} Promo code already assigned to user`);
        return false; // Already assigned
      }
      
      if (data.currentUses >= data.maxUses) {
        console.error(`[AUDIT] ${timestamp} Promo code max uses reached`);
        return false; // Max uses reached
      }
      
      await updateDoc(promoRef, {
        assignedTo: [...assignedTo, userId],
        currentUses: increment(1),
        updatedAt: Timestamp.now()
      });
      
      console.log(`[AUDIT] ${timestamp} Promo code assigned successfully`);
      return true;
    } catch (error) {
      console.error(`[AUDIT] ${timestamp} Error assigning promo code:`, error);
      return false;
    }
  },

  // ========== Analytics ==========

  /**
   * Get analytics events from Firestore for a specific period
   * @param period - 'today', 'week', or 'month'
   * @returns Array of event documents
   */
  getAnalyticsEvents: async (period: 'today' | 'week' | 'month' = 'week'): Promise<Array<{
    userId: string;
    screenName: string;
    featureName: string | null;
    timestamp: string;
  }>> => {
    const timestamp = new Date().toISOString();
    try {
      console.log(`[AUDIT] ${timestamp} Fetching analytics events for period: ${period}`);
      
      // Calculate date range based on period
      const now = new Date();
      const startDate = new Date(now);
      
      switch (period) {
        case 'today':
          startDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          startDate.setDate(startDate.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(startDate.getMonth() - 1);
          break;
      }
      
      const eventsRef = collection(db, 'analytics', 'events');
      const eventsQuery = query(
        eventsRef,
        where('timestamp', '>=', Timestamp.fromDate(startDate)),
        orderBy('timestamp', 'desc')
      );
      
      const snapshot = await getDocs(eventsQuery).catch(() => ({ size: 0, docs: [] }));
      
      const events = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          userId: data.userId || 'unknown',
          screenName: data.screenName || 'unknown',
          featureName: data.featureName || null,
          timestamp: toISOString(data.timestamp)
        };
      });
      
      console.log(`[AUDIT] ${timestamp} Retrieved ${events.length} analytics events`);
      return events;
    } catch (error) {
      console.error(`[AUDIT] ${timestamp} Error fetching analytics events:`, error);
      return [];
    }
  },

  /**
   * Get analytics data aggregated from multiple collections
   * @param period - 'today', 'week', or 'month' (default: 'week')
   */
  getAnalytics: async (period: 'today' | 'week' | 'month' = 'week'): Promise<Analytics> => {
    const timestamp = new Date().toISOString();
    try {
      console.log(`[AUDIT] ${timestamp} Fetching analytics from Firestore`);
      
      // Calculate date ranges
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      const monthAgo = new Date(today);
      monthAgo.setMonth(monthAgo.getMonth() - 1);

      // Get active users (users who logged in within time period)
      const usersRef = collection(db, 'users');
      const todayQuery = query(
        usersRef, 
        where('lastLogin', '>=', Timestamp.fromDate(today))
      );
      const weekQuery = query(
        usersRef, 
        where('lastLogin', '>=', Timestamp.fromDate(weekAgo))
      );
      const monthQuery = query(
        usersRef, 
        where('lastLogin', '>=', Timestamp.fromDate(monthAgo))
      );

      const [todaySnap, weekSnap, monthSnap, allUsersSnap] = await Promise.all([
        getDocs(todayQuery).catch(() => ({ size: 0, docs: [] })),
        getDocs(weekQuery).catch(() => ({ size: 0, docs: [] })),
        getDocs(monthQuery).catch(() => ({ size: 0, docs: [] })),
        getDocs(usersRef).catch(() => ({ size: 0, docs: [] }))
      ]);

      // Get crash logs from last 7 days
      const crashesRef = collection(db, 'crashLogs');
      const recentCrashesQuery = query(
        crashesRef,
        where('timestamp', '>=', Timestamp.fromDate(weekAgo)),
        orderBy('timestamp', 'desc'),
        limit(50)
      );
      const crashesSnap = await getDocs(recentCrashesQuery).catch(() => ({ size: 0, docs: [] }));

      // Process users for analytics
      const users = allUsersSnap.docs.map(doc => docToUser(doc, doc.id));
      
      // Calculate app version distribution
      const versionMap = new Map<string, number>();
      users.forEach(user => {
        const version = user.appVersion || 'unknown';
        versionMap.set(version, (versionMap.get(version) || 0) + 1);
      });
      const totalUsers = users.length;
      const appVersionDistribution = Array.from(versionMap.entries())
        .map(([version, count]) => ({
          version,
          userCount: count,
          percentage: totalUsers > 0 ? (count / totalUsers) * 100 : 0
        }))
        .sort((a, b) => b.userCount - a.userCount);

      // Calculate average session time
      const avgSessionTime = users.length > 0
        ? users.reduce((sum, u) => sum + (u.averageSessionTime || 0), 0) / users.length
        : 0;

      // Get analytics events for the selected period
      const events = await firebaseAdminAPI.getAnalyticsEvents(period);
      
      // Aggregate feature usage from events
      // Count feature usage: screenName + featureName combinations
      const featureUsageMap = new Map<string, number>();
      const screenViewMap = new Map<string, number>();
      
      events.forEach(event => {
        // Count screen views
        if (event.screenName) {
          screenViewMap.set(event.screenName, (screenViewMap.get(event.screenName) || 0) + 1);
        }
        
        // Count feature usage (only if featureName is not null)
        if (event.featureName) {
          // Format: "Screen: Feature" for better context
          const featureKey = `${event.screenName}: ${event.featureName}`;
          featureUsageMap.set(featureKey, (featureUsageMap.get(featureKey) || 0) + 1);
        }
      });
      
      // Convert to array and calculate percentages
      const totalFeatureEvents = Array.from(featureUsageMap.values()).reduce((sum, count) => sum + count, 0);
      const featureUsage = Array.from(featureUsageMap.entries())
        .map(([feature, count]) => ({
          feature,
          usageCount: count,
          percentage: totalFeatureEvents > 0 ? Math.round((count / totalFeatureEvents) * 100 * 10) / 10 : 0
        }))
        .sort((a, b) => b.usageCount - a.usageCount)
        .slice(0, 20); // Top 20 features
      
      // If no events, show empty array (no placeholder data)
      if (featureUsage.length === 0) {
        console.log(`[AUDIT] ${timestamp} No feature usage events found for period: ${period}`);
      }

      // Process crash logs
      const crashLogs = crashesSnap.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          userId: data.userId || 'unknown',
          timestamp: toISOString(data.timestamp),
          error: data.error || 'Unknown error',
          appVersion: data.appVersion || 'unknown',
          platform: data.platform || 'unknown'
        };
      });

      const analytics: Analytics = {
        activeUsers: {
          today: todaySnap.size || 0,
          week: weekSnap.size || 0,
          month: monthSnap.size || 0
        },
        uninstalls: {
          today: 0, // TODO: Implement uninstall tracking
          week: 0,
          month: 0
        },
        featureUsage,
        crashLogs,
        averageSessionTime: Math.round(avgSessionTime * 10) / 10, // Round to 1 decimal
        appVersionDistribution
      };

      console.log(`[AUDIT] ${timestamp} Analytics retrieved:`, {
        activeUsers: analytics.activeUsers,
        crashLogs: crashLogs.length
      });

      return analytics;
    } catch (error) {
      console.error(`[AUDIT] ${timestamp} Error fetching analytics:`, error);
      // Return empty analytics on error
      return {
        activeUsers: { today: 0, week: 0, month: 0 },
        uninstalls: { today: 0, week: 0, month: 0 },
        featureUsage: [],
        crashLogs: [],
        averageSessionTime: 0,
        appVersionDistribution: []
      };
    }
  }
};

